/**
 * stay/intelligence.ts — pure housing-intelligence functions.
 *
 * Every insight derives exclusively from RECORDED listings and their price
 * history — no market data is invented. Missing data ⇒ null, never zero.
 */

import type { HousingIntelligence, StayListing } from './models';

function average(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((s, v) => s + v, 0) / values.length;
}

/** Trend over recorded price points across a city's listings; needs ≥2 changes. */
export function computePriceTrend(listings: StayListing[]): 'rising' | 'falling' | 'stable' | null {
  const deltas: number[] = [];
  for (const l of listings) {
    const history = l.priceHistory;
    for (let i = 1; i < history.length; i++) {
      deltas.push(history[i].rentMonthly - history[i - 1].rentMonthly);
    }
  }
  if (deltas.length === 0) return null;
  const net = deltas.reduce((s, d) => s + d, 0);
  const base = average(
    listings.flatMap((l) => (l.rentMonthly != null ? [l.rentMonthly] : [])),
  );
  if (base == null || base === 0) return null;
  const pct = net / base;
  if (pct > 0.02) return 'rising';
  if (pct < -0.02) return 'falling';
  return 'stable';
}

export function computeHousingIntelligence(city: string, all: StayListing[]): HousingIntelligence {
  const listings = all.filter((l) => l.city === city);
  const rents = listings.flatMap((l) => (l.rentMonthly != null ? [l.rentMonthly] : []));

  const byNeighborhood = new Map<string, number[]>();
  for (const l of listings) {
    if (l.neighborhood == null || l.rentMonthly == null) continue;
    if (!byNeighborhood.has(l.neighborhood)) byNeighborhood.set(l.neighborhood, []);
    byNeighborhood.get(l.neighborhood)!.push(l.rentMonthly);
  }

  const internetKnown = listings.filter((l) => l.internetMbps != null);
  const noiseKnown = listings.filter((l) => l.noiseLevel != null);
  const recs = listings.flatMap((l) => (l.communityRecommendations != null ? [l.communityRecommendations] : []));

  return {
    city,
    listingCount: listings.length,
    averageRent: average(rents),
    averageRentByNeighborhood: Array.from(byNeighborhood.entries())
      .map(([neighborhood, values]) => ({
        neighborhood,
        averageRent: average(values)!,
        listingCount: values.length,
      }))
      .sort((a, b) => a.averageRent - b.averageRent),
    priceTrend: computePriceTrend(listings),
    averageInternetMbps: average(internetKnown.map((l) => l.internetMbps!)),
    verifiedInternetShare: internetKnown.length > 0
      ? internetKnown.filter((l) => l.internetVerified).length / internetKnown.length
      : null,
    quietShare: noiseKnown.length > 0
      ? noiseKnown.filter((l) => l.noiseLevel === 'quiet').length / noiseKnown.length
      : null,
    nomadRecommendations: recs.length > 0 ? recs.reduce((s, v) => s + v, 0) : null,
  };
}
